import React from 'react';
import { downloadCSV } from '_utils/csv-export';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

const titleStyle = {
    border: '1px solid #1e3a5f',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#1e3a5f',
    color: '#fff',
    fontWeight: 700,
    fontSize: '14px',
};

const sectionTitleStyle = {
    border: '1px solid #1e3a5f',
    padding: '8px 14px',
    textAlign: 'center',
    backgroundColor: '#1e3a5f',
    color: '#fff',
    fontWeight: 600,
    fontSize: '13px',
};

const groupHeaderStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'center',
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    fontSize: '13px',
    color: '#1f2937',
};

const tierHeaderStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 500,
    fontSize: '12px',
    color: '#374151',
};

const labelCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '12px',
    color: '#1f2937',
    backgroundColor: '#f3f4f6',
};

const valueCellStyle = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
};

const filterFiniteTiers = (tiers) => (tiers || []).filter((t) => t.mins != null);

const renderSection = (sectionLabel, preAdvised, nonAdvised, totalCols) => {
    const preTiers = filterFiniteTiers(preAdvised?.tiers);
    const nonTiers = filterFiniteTiers(nonAdvised?.tiers);
    const preColCount = preTiers.length + 1;
    const nonColCount = nonTiers.length + 1;

    return (
        <>
            <tr>
                <td style={sectionTitleStyle} colSpan={totalCols}>{sectionLabel}</td>
            </tr>
            <tr>
                <td style={groupHeaderStyle} colSpan={preColCount}>SLA Pre-Advised</td>
                <td style={groupHeaderStyle} colSpan={nonColCount}>SLA Non Pre-Advised</td>
            </tr>
            <tr>
                <td style={tierHeaderStyle}></td>
                {preTiers.map((t, i) => (
                    <td key={`pre-h-${i}`} style={tierHeaderStyle}>&lt;{t.mins}mins</td>
                ))}
                <td style={tierHeaderStyle}></td>
                {nonTiers.map((t, i) => (
                    <td key={`non-h-${i}`} style={tierHeaderStyle}>&lt;{t.mins}mins</td>
                ))}
            </tr>
            <tr>
                <td style={labelCellStyle}>Target</td>
                {preTiers.map((t, i) => (
                    <td key={`pre-t-${i}`} style={valueCellStyle}>{t.targetPct}%</td>
                ))}
                <td style={labelCellStyle}>Target</td>
                {nonTiers.map((t, i) => (
                    <td key={`non-t-${i}`} style={valueCellStyle}>{t.targetPct}%</td>
                ))}
            </tr>
            <tr>
                <td style={labelCellStyle}>Actual</td>
                {preTiers.map((t, i) => (
                    <td key={`pre-a-${i}`} style={valueCellStyle}>{t.countWithinPct ?? 0}%</td>
                ))}
                <td style={labelCellStyle}>Actual</td>
                {nonTiers.map((t, i) => (
                    <td key={`non-a-${i}`} style={valueCellStyle}>{t.countWithinPct ?? 0}%</td>
                ))}
            </tr>
        </>
    );
};

const BfsSlaTable = ({ departureSummary, arrivalSummary, startDate, endDate, departureError, arrivalError }) => {
    const depPre = filterFiniteTiers(departureSummary?.preAdvised?.tiers);
    const depNon = filterFiniteTiers(departureSummary?.nonAdvised?.tiers);
    const arrPre = filterFiniteTiers(arrivalSummary?.preAdvised?.tiers);
    const arrNon = filterFiniteTiers(arrivalSummary?.nonAdvised?.tiers);

    const maxPreCols = Math.max(depPre.length, arrPre.length, 1) + 1;
    const maxNonCols = Math.max(depNon.length, arrNon.length, 1) + 1;
    const totalCols = maxPreCols + maxNonCols;

    const hasAnyData = departureSummary || arrivalSummary;
    if (!hasAnyData) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No data available. Run the report to see results.</p>;
    }

    const buildSectionCsvRows = (label, preAdvised, nonAdvised) => {
        const preTiers = filterFiniteTiers(preAdvised?.tiers);
        const nonTiers = filterFiniteTiers(nonAdvised?.tiers);
        const rows = [];

        rows.push({ Section: label, Side: '', Metric: '', ...Object.fromEntries([...preTiers, ...nonTiers].map((t) => [`<${t.mins}mins`, ''])) });

        const targetRow = { Section: '', Side: 'Pre-Advised', Metric: 'Target' };
        preTiers.forEach((t) => { targetRow[`Pre <${t.mins}mins`] = `${t.targetPct}%`; });
        nonTiers.forEach((t) => { targetRow[`Non <${t.mins}mins`] = `${t.targetPct}%`; });
        rows.push(targetRow);

        const actualRow = { Section: '', Side: 'Pre-Advised', Metric: 'Actual' };
        preTiers.forEach((t) => { actualRow[`Pre <${t.mins}mins`] = `${t.countWithinPct ?? 0}%`; });
        nonTiers.forEach((t) => { actualRow[`Non <${t.mins}mins`] = `${t.countWithinPct ?? 0}%`; });
        rows.push(actualRow);

        return rows;
    };

    const handleExportCsv = () => {
        const rows = [];
        if (departureSummary) {
            rows.push(...buildSectionCsvRows('Departing Passengers', departureSummary.preAdvised, departureSummary.nonAdvised));
        }
        if (arrivalSummary) {
            rows.push(...buildSectionCsvRows('Arriving Passengers', arrivalSummary.preAdvised, arrivalSummary.nonAdvised));
        }
        downloadCSV(rows, `bfs-sla-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const handleExportExcel = async () => {
        const Excel = await import('exceljs/dist/exceljs.min.js');
        const workbook = new Excel.Workbook();
        const sheet = workbook.addWorksheet('BFS SLA');

        const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
            cell.border = border;
            cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
            if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
            if (font) cell.font = font;
        };

        const COLORS = {
            title: 'FF1E3A5F',
            section: 'FFF3F4F6',
            whiteText: 'FFFFFFFF',
            darkText: 'FF1F2937',
        };

        for (let c = 1; c <= totalCols; c++) {
            sheet.getColumn(c).width = 14;
        }

        const addTitleRow = (label) => {
            const row = sheet.addRow(Array(totalCols).fill(''));
            row.getCell(1).value = label;
            sheet.mergeCells(`A${row.number}:${String.fromCharCode(64 + totalCols)}${row.number}`);
            applyCell(row.getCell(1), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
            row.height = 26;
        };

        const addSectionToExcel = (sectionLabel, preAdvised, nonAdvised) => {
            const preTiers = filterFiniteTiers(preAdvised?.tiers);
            const nonTiers = filterFiniteTiers(nonAdvised?.tiers);
            const preColCount = preTiers.length + 1;
            const nonColCount = nonTiers.length + 1;

            const sectionRow = sheet.addRow(Array(totalCols).fill(''));
            sectionRow.getCell(1).value = sectionLabel;
            sheet.mergeCells(`A${sectionRow.number}:${String.fromCharCode(64 + totalCols)}${sectionRow.number}`);
            applyCell(sectionRow.getCell(1), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText }, size: 12 } });
            sectionRow.height = 24;

            const groupRow = sheet.addRow(Array(totalCols).fill(''));
            groupRow.getCell(1).value = 'SLA Pre-Advised';
            groupRow.getCell(preColCount + 1).value = 'SLA Non Pre-Advised';
            const preEnd = String.fromCharCode(64 + preColCount);
            const nonStart = String.fromCharCode(64 + preColCount + 1);
            const nonEnd = String.fromCharCode(64 + preColCount + nonColCount);
            sheet.mergeCells(`A${groupRow.number}:${preEnd}${groupRow.number}`);
            sheet.mergeCells(`${nonStart}${groupRow.number}:${nonEnd}${groupRow.number}`);
            applyCell(groupRow.getCell(1), { fill: COLORS.section, font: { bold: true, color: { argb: COLORS.darkText }, size: 11 } });
            applyCell(groupRow.getCell(preColCount + 1), { fill: COLORS.section, font: { bold: true, color: { argb: COLORS.darkText }, size: 11 } });
            groupRow.height = 22;

            const tierVals = ['', ...preTiers.map((t) => `<${t.mins}mins`), '', ...nonTiers.map((t) => `<${t.mins}mins`)];
            const tierRow = sheet.addRow(tierVals);
            for (let c = 1; c <= totalCols; c++) applyCell(tierRow.getCell(c), { fill: 'FFF9FAFB' });
            tierRow.height = 20;

            const targetVals = ['Target', ...preTiers.map((t) => `${t.targetPct}%`), 'Target', ...nonTiers.map((t) => `${t.targetPct}%`)];
            const targetRow = sheet.addRow(targetVals);
            for (let c = 1; c <= totalCols; c++) applyCell(targetRow.getCell(c));
            applyCell(targetRow.getCell(1), { fill: COLORS.section, font: { bold: true }, align: 'left' });
            applyCell(targetRow.getCell(preTiers.length + 2), { fill: COLORS.section, font: { bold: true }, align: 'left' });
            targetRow.height = 20;

            const actualVals = ['Actual', ...preTiers.map((t) => `${t.countWithinPct ?? 0}%`), 'Actual', ...nonTiers.map((t) => `${t.countWithinPct ?? 0}%`)];
            const actualRow = sheet.addRow(actualVals);
            for (let c = 1; c <= totalCols; c++) applyCell(actualRow.getCell(c));
            applyCell(actualRow.getCell(1), { fill: COLORS.section, font: { bold: true }, align: 'left' });
            applyCell(actualRow.getCell(preTiers.length + 2), { fill: COLORS.section, font: { bold: true }, align: 'left' });
            actualRow.height = 20;
        };

        addTitleRow('Service level Agreement KPIs PRM');
        if (departureSummary) addSectionToExcel('Departing Passengers', departureSummary.preAdvised, departureSummary.nonAdvised);
        if (arrivalSummary) addSectionToExcel('Arriving Passengers', arrivalSummary.preAdvised, arrivalSummary.nonAdvised);

        sanitizeExcelWorkbook(workbook);
        const buf = await workbook.xlsx.writeBuffer();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        link.download = `bfs-sla-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
                <button
                    type="button"
                    onClick={handleExportCsv}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                    }}
                >
                    Export CSV
                </button>
                <button
                    type="button"
                    onClick={handleExportExcel}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                    }}
                >
                    Export Excel
                </button>
            </div>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={titleStyle} colSpan={totalCols}>
                            Service level Agreement KPIs PRM
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {departureSummary ? (
                        renderSection('Departing Passengers', departureSummary.preAdvised, departureSummary.nonAdvised, totalCols)
                    ) : departureError ? (
                        <tr><td colSpan={totalCols} style={{ ...valueCellStyle, color: '#dc2626' }}>Departure data failed: {departureError}</td></tr>
                    ) : null}
                    {arrivalSummary ? (
                        renderSection('Arriving Passengers', arrivalSummary.preAdvised, arrivalSummary.nonAdvised, totalCols)
                    ) : arrivalError ? (
                        <tr><td colSpan={totalCols} style={{ ...valueCellStyle, color: '#dc2626' }}>Arrival data failed: {arrivalError}</td></tr>
                    ) : null}
                </tbody>
            </table>
        </div>
    );
};

export default BfsSlaTable;
